/**
 * Centralised dayjs plugin registration. Imported as the very first
 * statement in `index.tsx` so every plugin is attached to the singleton
 * dayjs prototype before any other module touches it.
 *
 * Why a single entry point: previous attempts to extend plugins inside
 * individual feature modules (e.g. `agenda/components/AddScheduleDialog.tsx`)
 * fell over under Vite + ESM. Vite's CommonJS interop wraps each plugin
 * import in a frozen namespace, and tricks like `delete relativeTime.$i`
 * throw silently in strict mode — leaving the prototype patches half-
 * applied and producing runtime errors like "fromNow is not a function" or
 * "isoWeekday(...).locale(...).format is not a function".
 *
 * dayjs.extend is idempotent, so leaving the per-feature `extend` calls in
 * place is harmless; this module just guarantees that *something* applies
 * the patches before render.
 */

import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isoWeek from 'dayjs/plugin/isoWeek'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import minMax from 'dayjs/plugin/minMax'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import updateLocale from 'dayjs/plugin/updateLocale'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'

import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/es'
import 'dayjs/locale/it'
import 'dayjs/locale/ar-sa'

// Custom relativeTime thresholds (kept in sync with utils/locale.ts) so short
// durations humanize correctly (e.g. 45 minutes stays "45 minutes" instead of
// rounding up to "an hour"). Inlined here to avoid pulling the i18n locale bundle
// into this early-loaded setup module.
const relativeTimeThresholds = [
  { l: 's', r: 1 },
  { l: 'm', r: 1 },
  { l: 'mm', r: 59, d: 'minute' },
  { l: 'h', r: 1 },
  { l: 'hh', r: 23, d: 'hour' },
  { l: 'd', r: 1 },
  { l: 'dd', r: 29, d: 'day' },
  { l: 'M', r: 1 },
  { l: 'MM', r: 11, d: 'month' },
  { l: 'y', r: 1 },
  { l: 'yy', d: 'year' },
]

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(calendar)
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isoWeek)
dayjs.extend(localeData)
dayjs.extend(localizedFormat)
dayjs.extend(minMax)
dayjs.extend(relativeTime, { thresholds: relativeTimeThresholds })
dayjs.extend(updateLocale)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)

